import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import {
  DefaultSelectComponentModel,
  SelectComponent
} from 'components/CampaignLimitation/SelectComponent';
import { defaultItemSetting } from 'components/CampaignLimitation/SelectItemComponent';
import Tags from 'components/common/Tags/Tags';
import {
  DEVICE_FILTER_OPTIONS_EMPTY_I18N,
  DeviceFilterOptionsState,
  findFilterOptions
} from './deviceFilters';
import styles from './deviceManagement.module.scss';

type DeviceFilterSelectProps = {
  /** 同時當 trigger 的 id，所以一頁裡要唯一。 */
  name: string;
  label: string;
  /** 這個條件的行為提示，開起來就看得到。 */
  hint: string;
  options: SelectOptions[];
  /**
   * 選項取回來了沒。空清單在三種狀態下寫的是三句不同的話：還在載入、沒有符合的選項、
   * 載入失敗 —— 畫面上都是一片空白，但使用者接下來該做的事不一樣。
   */
  optionsState: DeviceFilterOptionsState;
  /**
   * 還沒選銷售渠道。選項是「這個渠道底下有哪些值」，沒有渠道就沒有選項可挑；
   * 而且換渠道會把已選的條件清空，這時讓人先選反而會被默默丟掉。
   */
  disabled?: boolean;
  values: string[];
  onChange: (values: string[]) => void;
};

/**
 * 機台管理頁首的篩選下拉。
 *
 * 選項本體用的是廣告活動版位選擇器那一套 `SelectComponent`：搜尋列、可勾的父層、
 * 展開後的子項、虛擬捲動、頁腳計數都在那邊，這裡只補一顆 trigger 與外面的浮層。
 * 那一套本來就是這個 repo 的階層選取樣式，選到的項目會直接在清單上變成已選狀態，
 * 而不是只剩一個計數。
 *
 * 已選的項目再用 `Tags` 排在條件本身之後，與版位限制那邊「選完長什麼樣」一致。
 *
 * 進出的值：畫面這一層存的是要送給後端的字串，`SelectComponent` 進出都是
 * `SelectOptions`，所以兩邊各換一次。
 */
export const DeviceFilterSelect: React.FC<DeviceFilterSelectProps> = ({
  name,
  label,
  hint,
  options,
  optionsState,
  disabled = false,
  values,
  onChange
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  const selectedOptions = useMemo(
    () => findFilterOptions(options, values),
    [options, values]
  );

  const onSelectionChange = useCallback(
    (selected: SelectOptions[]) =>
      onChange(selected.map((option) => String(option.value))),
    [onChange]
  );

  // 每次 render 重建 model，與 LimitationSetting 同一套用法：SelectComponent 在
  // model 換人時會把搜尋字串與展開狀態接過去（見它的 componentDidUpdate）。
  const model = new DefaultSelectComponentModel(
    name,
    false,
    defaultItemSetting(),
    i18n.t('deviceManagement.placeholders.filterSearch'),
    onSelectionChange,
    options,
    selectedOptions,
    undefined,
    false,
    DEVICE_FILTER_OPTIONS_EMPTY_I18N[optionsState],
    'limitation.classificationTotal'
  );

  const renderHint = useCallback(
    () => <span className={styles.filterHint}>{hint}</span>,
    [hint]
  );

  return (
    <div className={styles.filter} ref={containerRef}>
      <button
        type='button'
        id={name}
        className={
          open || values.length > 0
            ? `${styles.filterTrigger} ${styles.filterTriggerActive}`
            : styles.filterTrigger
        }
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={styles.filterTriggerLabel}>{label}</span>
        {values.length > 0 && (
          <span
            className={styles.filterCount}
            data-testid={`filter-count-${name}`}
          >
            {values.length}
          </span>
        )}
        <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} />
      </button>
      {open && (
        <div className={styles.filterMenu} data-testid={`filter-menu-${name}`}>
          <SelectComponent model={model} renderFooter={renderHint} />
        </div>
      )}
    </div>
  );
};

/** 已選項目排成一列時，每一筆要記得自己原本屬於哪一個條件。 */
export type DeviceFilterTag = SelectOptions & { field: string };

type DeviceFilterTagsProps = {
  values: DeviceFilterTag[];
  /** 移除之後剩下的項目。四個條件混在同一排，所以由呼叫端拆回各自的欄位。 */
  onChange: (remaining: DeviceFilterTag[]) => void;
};

/**
 * 條件列上的已選項目。用的是版位限制那邊同一支 `Tags`，移除的互動一致。
 */
export const DeviceFilterTags: React.FC<DeviceFilterTagsProps> = ({
  values,
  onChange
}) => (
  <div className={styles.filterTags} data-testid='device-filter-tags'>
    <Tags
      value={values as any}
      onChange={onChange as any}
      disableInput
      showPlus={false}
    />
  </div>
);
